/**
 * 도메인 타입 — 순수 데이터. React·DOM·Canvas 를 모른다.
 *
 * 트라이얼은 "재생 가능한 데이터"이지 코드가 아니다. 시드 하나로 같은 트라이얼 리스트가
 * 다시 나오고, 실행 중에는 어떤 무작위 연산도 일어나지 않는다.
 */

export type TaskKind = 'CBM_A' | 'CBM_I' | 'CBM_AAT' | 'TALKING_HEADS';
export type ResponseCode = 'top' | 'bottom' | 'left' | 'right';
export type InputSource = 'keyboard' | 'pointer' | 'gamepad';
export type ResponseMode = 'vertical' | 'horizontal';

/**
 * 그리기 명령. 엔진이 해석한다. 자극(bitmap)은 반드시 사전 렌더된 에셋 id 로만 가리킨다 —
 * 트라이얼 중에 텍스트 레이아웃이나 이미지 디코딩이 일어나면 안 되기 때문이다.
 * 좌표는 논리 무대(stage) 단위. 무대 크기는 STAGE_W × STAGE_H.
 */
export type RenderOp =
  | { readonly op: 'clear' }
  | { readonly op: 'fixation'; readonly x: number; readonly y: number; readonly size: number }
  | { readonly op: 'bitmap'; readonly assetId: string; readonly x: number; readonly y: number }
  | { readonly op: 'probe'; readonly kind: 'two-dot' | 'one-dot'; readonly x: number; readonly y: number; readonly size: number }
  | { readonly op: 'feedback' }   // 직전 트라이얼의 정오에 따라 엔진이 그린다
  | { readonly op: 'text'; readonly text: string; readonly x: number; readonly y: number; readonly size: number };

export const STAGE_W = 1000;
export const STAGE_H = 700;

export interface Phase {
  readonly id: string;
  /** ms 로 표현한다. 프레임 환산은 런타임(frameMs)에 엔진이 한다. */
  readonly durationMs: number | 'until-response';
  readonly timeoutMs?: number;
  readonly acceptsInput: boolean;
  /** 이 페이즈의 온셋이 RT 의 기준점이다 */
  readonly rtAnchor?: boolean;
  readonly ops: readonly RenderOp[];
}

export interface TrialSpec<M = unknown> {
  readonly index: number;
  readonly blockId: string;
  readonly phases: readonly Phase[];
  readonly correctResponse: ResponseCode | null;
  readonly isTraining: boolean;
  readonly meta: M;
}

export interface PhaseTiming {
  id: string;
  requestedMs: number;
  frames: number;
  /** 페이즈 진입 프레임의 rAF 타임스탬프 간 차이. vsync 기준 실측. */
  actualMs: number;
  droppedFrames: number;
}

export interface TrialResult {
  trialIndex: number;
  blockId: string;
  /** rtAnchor 페이즈의 추정 온셋 (rAF ts + 1 frame). null 이면 그 페이즈에 도달하지 못함. */
  onsetTs: number | null;
  responseTs: number | null;
  rt: number | null;
  response: ResponseCode | null;
  correct: boolean | null;
  timedOut: boolean;
  inputSource: InputSource | null;
  /** 이벤트 생성 → 핸들러 실행. 순수 기계값. */
  handlerDelayMs: number | null;
  /** 반응 창 밖에서 들어온 입력 수 (선행 반응) */
  prematureInputs: number;
  phases: PhaseTiming[];
  aborted: boolean;
}

/** 논리 무대 위의 자극 배치. 엔진이 물리 캘리브레이션으로부터 계산해 도메인에 넘긴다. */
export interface StageGeometry {
  readonly centerX: number;
  readonly centerY: number;
  /** 자극쌍의 중심 간 거리 (무대 단위) */
  readonly separation: number;
  readonly stimulusHeight: number;
  readonly fixationSize: number;
  readonly probeSize: number;
}
